import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/_new/Button';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useAuth } from 'contexts/authContext';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';
import { Skeleton } from 'primereact/skeleton';
import { KnowledgeBaseArticleFlags } from 'types/KnowledgeBase';
import { FavoriteIcon } from './FavoriteIcon';
import { ToreadIcon } from './ToreadIcon';
import { ReadedIcon } from './ReadedIcon';

type ArticlePresenterFlagsProps = {
	articleContentId: number;
	initialFlags?: KnowledgeBaseArticleFlags;
	readed?: boolean;
	onFlagChange?: () => void;
	iconOnly?: boolean;
	applicationId?: number;
};

export const ArticlePresenterFlags = ({
	articleContentId,
	initialFlags,
	onFlagChange,
	readed,
	iconOnly,
	applicationId,
}: ArticlePresenterFlagsProps) => {
	const { t } = useTranslation();

	const { toastRef } = useGlobal();
	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();

	const [currentFlags, setCurrentFlags] = useState<KnowledgeBaseArticleFlags>(
		initialFlags || {
			favorite: false,
			readed: false,
			toRead: false,
		}
	);

	useEffect(() => {
		if (initialFlags) {
			setCurrentFlags(initialFlags);
		}
	}, [initialFlags]);

	const {
		data: flagsData,
		isLoading: currentFlagsLoading,
		refetch: currentFlagsRefetch,
	} = useQuery(
		['KNOWLEDGE_BASE_ARTICLE_FLAGS', { userId: Number(currentUser?.id), articleContentId }],
		() =>
			knowledgeBaseApi.application.getArticleContentFlags(
				articleContentId,
				Number(applicationId || currentApplicationData?.id)
			),
		{
			initialData: initialFlags,
			enabled: true,
			onSuccess: (data) => {
				if (data) {
					setCurrentFlags(data);
				}
			},
		}
	);

	const queryClient = useQueryClient();

	const {
		mutate: modifyFlagMutate,
		isLoading: modifyFlagLoading,
		variables: modifyFlagVariables,
	} = useMutation(
		(flags: { favorite?: boolean; toRead?: boolean; readed?: boolean }) =>
			knowledgeBaseApi.application.modifyArticleContentFlag(
				articleContentId,
				Number(applicationId || currentApplicationData?.id),
				flags
			),
		{
			onMutate: (variables) => {
				setCurrentFlags((prev) => ({ ...prev, ...variables }));
			},
			onSuccess: (response, variables) => {
				const { favorite, toRead, readed } = variables;
				if (typeof favorite !== 'undefined') {
					toastRef.current?.show({
						severity: 'success',
						life: 5000,
						summary: t('misc.success'),
						detail: favorite
							? t('knowledgeBase.favorite.addedToFavorites')
							: t('knowledgeBase.favorite.removedFromFavorites'),
					});
					queryClient.invalidateQueries({
						queryKey: ['videocall-favorite-articles', Number(applicationId || currentApplicationData?.id)],
					});
				}
				if (typeof toRead !== 'undefined') {
					toastRef.current?.show({
						severity: 'success',
						life: 5000,
						summary: t('misc.success'),
						detail: toRead
							? t('knowledgeBase.toRead.addedToToRead')
							: t('knowledgeBase.toRead.removedFromToRead'),
					});
				}
				if (typeof readed !== 'undefined') {
					toastRef.current?.show({
						severity: 'success',
						life: 5000,
						summary: t('misc.success'),
						detail: readed
							? t('knowledgeBase.readed.markedAsRead')
							: t('knowledgeBase.readed.unmarkedAsRead'),
					});
				}
				onFlagChange?.();
				currentFlagsRefetch();
			},
		}
	);

	const favoriteLoading = modifyFlagLoading && typeof modifyFlagVariables?.favorite !== 'undefined';
	const toReadLoading = modifyFlagLoading && typeof modifyFlagVariables?.toRead !== 'undefined';
	const readedLoading = modifyFlagLoading && typeof modifyFlagVariables?.readed !== 'undefined';

	const handleFavoriteClick = useCallback(() => {
		modifyFlagMutate({ favorite: !currentFlags.favorite });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFlags]);

	const handleBookmarkClick = useCallback(() => {
		modifyFlagMutate({ toRead: !currentFlags.toRead });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFlags]);

	const handleReadedClick = useCallback(() => {
		modifyFlagMutate({ readed: !currentFlags.readed });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFlags]);

	useEffect(() => {
		if (typeof readed !== 'undefined') {
			setCurrentFlags((prev) => ({ ...prev, readed }));
		}
	}, [readed]);

	if (currentFlagsLoading && !initialFlags) {
		return (
			<div className="flex flex-wrap gap-2 align-items-center justify-content-start article-flag-container">
				<Skeleton shape="rectangle" width={iconOnly ? '32px' : '150px'} height="32px" borderRadius="8px" />
				<Skeleton shape="rectangle" width={iconOnly ? '32px' : '160px'} height="32px" borderRadius="8px" />
				<Skeleton shape="rectangle" width={iconOnly ? '32px' : '140px'} height="32px" borderRadius="8px" />
			</div>
		);
	}

	return (
		<div className="flex flex-wrap gap-2 align-items-center justify-content-start article-flag-container">
			<div>
				<Button
					label={
						currentFlags.favorite
							? t('knowledgeBase.favorite.removeFromFavorites')
							: t('knowledgeBase.favorite.addToFavorites')
					}
					onClick={handleFavoriteClick}
					disabled={currentFlagsLoading || favoriteLoading}
					variant="transparent"
					icon={() => <FavoriteIcon checked={currentFlags.favorite} />}
					className="p-1"
					variantSize="sm"
					iconOnly={iconOnly}
					title={
						currentFlags.favorite
							? t('knowledgeBase.favorite.removeFromFavorites')
							: t('knowledgeBase.favorite.addToFavorites')
					}
				/>
			</div>
			<div>
				<Button
					label={
						currentFlags.toRead
							? t('knowledgeBase.toRead.removeFromToRead')
							: t('knowledgeBase.toRead.addToToRead')
					}
					onClick={handleBookmarkClick}
					disabled={currentFlagsLoading || toReadLoading}
					variant="transparent"
					icon={() => <ToreadIcon checked={currentFlags.toRead} />}
					className="p-1"
					variantSize="sm"
					iconOnly={iconOnly}
					title={
						currentFlags.toRead
							? t('knowledgeBase.toRead.removeFromToRead')
							: t('knowledgeBase.toRead.addToToRead')
					}
				/>
			</div>
			<div>
				<Button
					label={
						currentFlags.readed
							? t('knowledgeBase.readed.unmarkAsRead')
							: t('knowledgeBase.readed.markAsRead')
					}
					onClick={handleReadedClick}
					disabled={currentFlagsLoading || readedLoading}
					variant="transparent"
					icon={() => <ReadedIcon checked={currentFlags.readed} />}
					className="p-1"
					variantSize="sm"
					iconOnly={iconOnly}
					title={
						currentFlags.readed
							? t('knowledgeBase.readed.unmarkAsRead')
							: t('knowledgeBase.readed.markAsRead')
					}
				/>
			</div>
		</div>
	);
};
